import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { BiCheckCircle, BiEnvelopeOpen, BiErrorCircle, BiHourglass, BiPlus } from 'react-icons/bi';
import { platformAPI } from '../../services/platformAPI';
import WorkspaceDataTable from '../../components/workspace/WorkspaceDataTable';
import WorkspaceModal from '../../components/workspace/WorkspaceModal';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useAdminRecordDelete } from '../../components/workspace/useAdminRecordDelete';
import {
  SUPPORT_CATEGORIES,
  SUPPORT_PRIORITIES,
  SUPPORT_STATUSES,
  categoryLabel,
  moduleLabel,
  priorityVariant,
  statusVariant,
  supportBasePath
} from '../../config/workspaceSupport';
import './WorkspaceSupportPage.scss';

const EMPTY_FILTERS = { status: '', priority: '', category: '', search: '' };

const WorkspaceSupportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [createdBanner, setCreatedBanner] = useState(Boolean(location.state?.created));
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useDocumentTitle('Support');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, ticketsRes] = await Promise.all([
        platformAPI.getSupportStats(),
        platformAPI.listSupportTickets({ limit: 200 })
      ]);
      setStats(statsRes.data?.data?.stats || null);
      setTickets(ticketsRes.data?.data?.tickets || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Unable to load support desk');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const admin = useAdminRecordDelete({
    entity: 'support',
    noun: 'support request',
    plural: 'support requests',
    reload: load,
    label: (row) => row.title || row.ticketNumber
  });

  const openDetail = async (row) => {
    setDetailLoading(true);
    setDetail({ summary: row });
    try {
      const r = await platformAPI.getSupportTicket(row._id);
      setDetail({ ticket: r.data?.data?.ticket });
    } catch {
      setDetail({ ticket: row });
    } finally {
      setDetailLoading(false);
    }
  };

  const tableRows = useMemo(
    () => tickets.map((t) => {
      const createdBy = t.createdBy;
      const raisedBy = typeof createdBy === 'object' ? createdBy?.name || createdBy?.email : '—';
      return {
        ...t,
        subjectLine: t.title,
        moduleLine: moduleLabel(t.subcategory),
        categoryLine: categoryLabel(t.category),
        raisedBy,
        createdLabel: new Date(t.createdAt).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })
      };
    }),
    [tickets]
  );

  const visibleRows = useMemo(
    () => tableRows.filter((row) => {
      if (filters.status && row.status !== filters.status) return false;
      if (filters.priority && row.priority !== filters.priority) return false;
      if (filters.category && row.category !== filters.category) return false;
      return true;
    }),
    [tableRows, filters]
  );

  const attention = useMemo(
    () => tickets
      .filter((ticket) => !['RESOLVED', 'CLOSED'].includes(ticket.status))
      .slice()
      .sort((a, b) => {
        const rank = { CRITICAL: 0, URGENT: 1, HIGH: 2, MEDIUM: 3, LOW: 4 };
        return (rank[a.priority] ?? 5) - (rank[b.priority] ?? 5);
      })
      .slice(0, 6),
    [tickets]
  );

  const columns = useMemo(
    () => [
      {
        key: 'ticketNumber',
        label: 'Ticket no.',
        mobileTitle: true,
        render: (value) => <span className="ws-support__ticket-no">{value}</span>
      },
      {
        key: 'subjectLine',
        label: 'Subject',
        render: (value, row) => (
          <div className="ws-support__subject">
            <span className="ws-support__subject-title">{value}</span>
            <span className="ws-support__subject-meta">
              {row.moduleLine} · {row.categoryLine}
            </span>
          </div>
        )
      },
      {
        key: 'priority',
        label: 'Priority',
        render: (value) => (
          <span className={`ws-support__pill ws-support__pill--${priorityVariant(value)}`}>
            {SUPPORT_PRIORITIES.find((p) => p.value === value)?.label || value}
          </span>
        )
      },
      {
        key: 'status',
        label: 'Status',
        render: (value) => (
          <span className={`ws-support__pill ws-support__pill--${statusVariant(value)}`}>
            {SUPPORT_STATUSES.find((s) => s.value === value)?.label || value}
          </span>
        )
      },
      { key: 'raisedBy', label: 'Raised by' },
      { key: 'createdLabel', label: 'Created' }
    ],
    []
  );

  if (loading && !tickets.length) {
    return (
      <div className="ws-support py-5 text-center text-muted">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading help desk…
      </div>
    );
  }

  return (
    <div className="ws-support">
      <WorkspacePageHeader
        title="Support"
        subtitle="Help desk for your organisation — track issues, requests, and platform questions in one place."
        actions={
          <Button variant="primary" size="sm" onClick={() => navigate(`${supportBasePath(location.pathname)}/new`)}>
            <BiPlus className="me-1" />
            New ticket
          </Button>
        }
      />

      {createdBanner ? (
        <Alert variant="success" dismissible onClose={() => setCreatedBanner(false)}>
          Your support ticket is on the desk.
        </Alert>
      ) : null}
      {error && tickets.length ? (
        <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>
      ) : null}

      {error && !tickets.length ? (
        <section className="ws-support__unavailable">
          <h2>The desk did not load</h2>
          <p>{error}</p>
          <Button variant="primary" size="sm" onClick={load}>Try again</Button>
        </section>
      ) : null}

      {!error || tickets.length ? (
      <>
      <section className="ws-support__tiles" aria-label="Ticket summary">
        <article className="ws-support__tile">
          <div>
            <strong>{stats?.open ?? 0}</strong>
            <span>Open</span>
            <small>Waiting to be picked up</small>
          </div>
          <i aria-hidden><BiEnvelopeOpen /></i>
        </article>
        <article className="ws-support__tile">
          <div>
            <strong>{stats?.inProgress ?? 0}</strong>
            <span>In progress</span>
            <small>With the support team</small>
          </div>
          <i aria-hidden><BiHourglass /></i>
        </article>
        <article className={`ws-support__tile${(stats?.criticalOpen ?? 0) > 0 ? ' ws-support__tile--alert' : ''}`}>
          <div>
            <strong>{stats?.criticalOpen ?? 0}</strong>
            <span>Critical</span>
            <small>Highest priority, still open</small>
          </div>
          <i aria-hidden><BiErrorCircle /></i>
        </article>
        <article className="ws-support__tile">
          <div>
            <strong>{stats?.resolved ?? 0}</strong>
            <span>Resolved</span>
            <small>Closed this period</small>
          </div>
          <i aria-hidden><BiCheckCircle /></i>
        </article>
      </section>

      <div className="ws-support__desk">
        <div className="ws-support__main">
          <div className="ws-support__filters">
            <Form.Select
              aria-label="Status"
              value={filters.status}
              onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
            >
              <option value="">All statuses</option>
              {SUPPORT_STATUSES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Form.Select>
            <Form.Select
              aria-label="Priority"
              value={filters.priority}
              onChange={(event) => setFilters((current) => ({ ...current, priority: event.target.value }))}
            >
              <option value="">All priorities</option>
              {SUPPORT_PRIORITIES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Form.Select>
            <Form.Select
              aria-label="Category"
              value={filters.category}
              onChange={(event) => setFilters((current) => ({ ...current, category: event.target.value }))}
            >
              <option value="">All categories</option>
              {SUPPORT_CATEGORIES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Form.Select>
            {filters.status || filters.priority || filters.category ? (
              <button type="button" className="ws-support__clear" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear
              </button>
            ) : null}
          </div>

          {admin.notice}
          <WorkspaceDataTable
            className="ws-support__table"
            title="Support tickets"
            data={visibleRows}
            columns={columns}
            searchPlaceholder="Search ticket, subject, person…"
            showActions={admin.enabled}
            exportable
            pageSize={12}
            emptyMessage="No tickets match. Raise one when you need the team."
            onRowClick={(row) => openDetail(row)}
            {...admin.tableProps}
            rowActions={admin.wrapActions(() => [])}
          />
          {admin.modal}
        </div>

        <aside className="ws-support__rail">
          <header>
            <h2>Needs attention</h2>
            <span>{attention.length}</span>
          </header>
          {attention.length ? (
            <ul>
              {attention.map((ticket) => (
                <li key={ticket._id}>
                  <button type="button" onClick={() => openDetail(ticket)}>
                    <span className={`ws-support__mark ws-support__mark--${priorityVariant(ticket.priority)}`} aria-hidden>
                      {String(ticket.priority || 'M').slice(0, 1)}
                    </span>
                    <span>
                      <strong>{ticket.title}</strong>
                      <small>{ticket.ticketNumber} · {SUPPORT_STATUSES.find((item) => item.value === ticket.status)?.label || ticket.status}</small>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ws-support__rail-empty">Nothing is waiting.</p>
          )}
        </aside>
      </div>
      </>
      ) : null}

      <WorkspaceModal
        show={Boolean(detail)}
        onHide={() => setDetail(null)}
        title={detail?.ticket?.title || detail?.summary?.title || 'Ticket'}
        description={detail?.ticket?.ticketNumber || detail?.summary?.ticketNumber}
        size="lg"
        footer={
          <Button variant="outline-secondary" onClick={() => setDetail(null)}>Close</Button>
        }
      >
        {detailLoading ? (
          <div className="text-center py-4"><Spinner size="sm" animation="border" /></div>
        ) : detail?.ticket ? (
          <div className="ws-support__detail">
            <div className="ws-support__detail-badges">
              <span className={`ws-support__pill ws-support__pill--${statusVariant(detail.ticket.status)}`}>
                {SUPPORT_STATUSES.find((s) => s.value === detail.ticket.status)?.label}
              </span>
              <span className={`ws-support__pill ws-support__pill--${priorityVariant(detail.ticket.priority)}`}>
                {SUPPORT_PRIORITIES.find((p) => p.value === detail.ticket.priority)?.label}
              </span>
            </div>
            <p className="ws-support__detail-desc">{detail.ticket.description}</p>
            <dl className="ws-support__detail-meta">
              <div><dt>Category</dt><dd>{categoryLabel(detail.ticket.category)}</dd></div>
              <div><dt>Module</dt><dd>{moduleLabel(detail.ticket.subcategory)}</dd></div>
              <div><dt>Raised by</dt><dd>{detail.ticket.createdBy?.name || detail.ticket.createdBy?.email || '—'}</dd></div>
              <div><dt>Assigned</dt><dd>{detail.ticket.assignedTo?.name || 'Unassigned'}</dd></div>
              <div><dt>Created</dt><dd>{new Date(detail.ticket.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</dd></div>
            </dl>
            {(detail.ticket.messages || []).some((item) => !item.isInternal) ? (
              <div className="ws-support__thread">
                <h3>Conversation</h3>
                <ol>
                  {(detail.ticket.messages || []).filter((item) => !item.isInternal).map((item) => (
                    <li key={item._id || `${item.createdAt}-${item.message}`}>
                      <span className="ws-support__thread-who">
                        {item.sender?.name || 'Support'}
                        <time>{item.createdAt ? new Date(item.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : ''}</time>
                      </span>
                      <p>{item.message}</p>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        ) : null}
      </WorkspaceModal>
    </div>
  );
};

export default WorkspaceSupportPage;
